import React, { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { Tooltip, Box } from "@mui/material";
import { useGetPlatformsQuery } from "state/rtkQuery/platform/platformQueries";
import { useUpdateUserMembershipBulkMutation, useUpdateUserOrgStatusMutation } from "state/rtkQuery/user/userMutations";
import { useGetUsersByOrgAndStatusQuery } from "state/rtkQuery/user/userQueries";
import { EMAIL_COLUMN_CHARACTER_TRUNCATE_LIMIT_OWNER_VIEW } from "utils/constants";
import { generateRandomColor } from "utils/generateRandomColor";
import { useDebounce } from "utils/useDebounce";

import AvatarRandomColor from "../ReusableDataGrid/AvatarRandomColor";
import ReusableActions from "../ReusableDataGrid/ReusableActions";
import ReusableDataGrid from "../ReusableDataGrid/ReusableDataGrid";
import { useTabSortFilter } from "../hooks/useTabSortFilter";

import "../TabStyles.css";

const RejectedTab = ({ userRejectedHistoryMockList, orgName }) => {
  const [columns, setColumns] = useState([]);
  const [updatedRows, setUpdatedRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [query, setQuery] = useState("");
  const { orgId } = useParams();
  const { data: platformData, isFetching: isPlatformDataLoading } =
    useGetPlatformsQuery();

  // Filter options for this tab
  const filterOptions = useMemo(() => [
    { value: "first_name", label: "First Name" },
    { value: "last_name", label: "Last Name" },
    { value: "changed_date", label: "Date of Rejection" },
    { value: "changed_by", label: "Rejected By" },
  ], []);

  // Use the sorting and filtering hook
  const {
    sortBy,
    sortOrder,
    formattedFilterValue,
    filterField,
    nameSortMenuAnchor,
    debouncedEmailSearch,
    handleNameSortMenuOpen,
    renderNameSortMenu,
    renderNameSortIcon,
    renderSortIcon,
    renderFilterFieldSelect,
    renderDynamicSearchInput,
  } = useTabSortFilter(filterOptions, {}, setCurrentPage);

  const {
    data: rejectedUserListData,
    isFetching: isRejectedUserListDataLoading,
  } = useGetUsersByOrgAndStatusQuery(
    {
      orgId: orgId,
      status: "Rejected",
      platformId: platformData?._id,
      page: currentPage,
      perPage: 25,
      email: debouncedEmailSearch === "" ? undefined : debouncedEmailSearch,
      sortBy: sortBy || undefined,
      sortOrder: sortOrder || undefined,
      filterField: filterField || undefined,
      filterValue: formattedFilterValue === "" ? undefined : formattedFilterValue,
    },
    { skip: !platformData?._id, refetchOnMountOrArgChange: true }
  );

  // Set datagrid rows
  useEffect(() => {
    if (rejectedUserListData) {
      setUpdatedRows(rejectedUserListData?.users);
    }
  }, [rejectedUserListData]);

  // Bulk mutation
  const [updateUserMembershipBulk, { isLoading: isUpdateUserMembershipBulkLoading }] =
    useUpdateUserMembershipBulkMutation();

  const userEmail = useSelector((state) => state.auth.userAccount?.username);

  // Bulk handler
  const handleBulkAction = async (userIds, rejectReason, otherReason) => {
    try {
      const bulkPayload = {
        platforms: {
          [platformData?._id]: {
            organizations: {
              [orgId]: {
                membership_status: "Not-Requested",
                reason: rejectReason === "Other" ? otherReason : rejectReason,
                changed_by: userEmail?.toLowerCase(),
              },
            },
          },
        },
        user_ids: userIds,
      };
      await updateUserMembershipBulk({
        body: bulkPayload,
        invalidateStatuses: ["Rejected"],
      });
    } catch (error) {
      // Handle error
    }
  };

  // Single user action with status check - use single-user mutation when acting on one user
  const [updateUserOrgStatus] = useUpdateUserOrgStatusMutation();

  const singleUserAction = (params) => async (rejectReason, otherReason) => {
    if (params.row.status === "rejected") {
      try {
        const singlePayload = {
          platforms: {
            [platformData?._id]: {
              organizations: {
                [orgId]: {
                  membership_status: "Not-Requested",
                  reason: rejectReason === "Other" ? otherReason : rejectReason,
                  changed_by: userEmail?.toLowerCase(),
                },
              },
            },
          },
        };

        await updateUserOrgStatus({
          userId: params.row.id,
          body: singlePayload,
          attribute: "membership_status",
        }).unwrap();
      } catch (err) {
        // fallback to bulk as a last resort
        handleBulkAction([params.row.id], rejectReason, otherReason);
      }
    }
  };

  // Multi user action
  const multiUserAction = (userIds, rejectReason, otherReason) => {
    handleBulkAction(userIds, rejectReason, otherReason);
  };
  useEffect(() => {
    if (updatedRows) {
      setColumns([
        {
          field: "name",
          headerName: `Showing ${(currentPage - 1) * 25 + 1} - ${(currentPage - 1) * 25 + updatedRows?.length
            } of ${rejectedUserListData?.total || 0} Users`,
          headerClassName: "history-tab-totalCount-header",
          headerAlign: "left",
          align: "left",
          flex: 2,
          minWidth: 270,
          sortable: false,
          renderHeader: () => (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.25,
                overflow: "hidden",
                width: "100%",
                position: "relative",
                pr: 0,
                "&:hover": { pr: "30px" },
                "&:hover .sort-icon": { opacity: 1 },
              }}
            >
              <span 
                className="history-tab-totalCount-header"
                style={{ 
                  overflow: "hidden", 
                  textOverflow: "ellipsis", 
                  whiteSpace: "nowrap",
                  flex: 1,
                  minWidth: 0
                }}
              >
                {`Showing ${(currentPage - 1) * 25 + 1} - ${(currentPage - 1) * 25 + updatedRows?.length} of ${rejectedUserListData?.total || 0} Users`}
              </span>
              {renderNameSortIcon()}
              {renderNameSortMenu(setCurrentPage)}
            </Box>
          ),
          renderCell: (params) => (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                flexDirection: "row",
              }}
            >
              <AvatarRandomColor
                firstName={params.row.firstName}
                lastName={params.row.lastName}
                color={params.row.color}
              />
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  marginLeft: "20px",
                }}
              >
                <Tooltip
                  title={
                    <span style={{ fontSize: "12px" }}>{params.row.name}</span>
                  }
                  placement="top-start"
                  arrow
                >
                  <span
                    className="table-history-userEmailJoined-cell"
                    style={{ fontWeight: "600", fontSize: "14px" }}
                  >
                    {params.row.name}
                  </span>
                </Tooltip>
                <Tooltip
                  title={
                    <span style={{ fontSize: "12px" }}>{params.row.email}</span>
                  }
                  arrow
                  placement="bottom-start"
                >
                  {params.row.email.length >
                    EMAIL_COLUMN_CHARACTER_TRUNCATE_LIMIT_OWNER_VIEW ? (
                    <div>
                      <span
                        className="table-cell-trucate-reason"
                        style={{ paddingLeft: "0", fontSize: "13px" }}
                      >
                        {params.row.email?.slice(
                          0,
                          EMAIL_COLUMN_CHARACTER_TRUNCATE_LIMIT_OWNER_VIEW
                        ) + "..."}
                      </span>{" "}
                    </div>
                  ) : (
                    <span style={{ fontSize: "13px" }}>{params.row.email}</span>
                  )}
                </Tooltip>
              </div>
            </div>
          ),
        },
        {
          field: "DOJ",
          headerName: "Date of Rejection",
          headerClassName: "date-rejected-table-header",
          headerAlign: "center",
          align: "center",
          flex: 1,
          minWidth: 140,
          sortable: false,
          renderHeader: () => (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.25,
                overflow: "hidden",
                width: "100%",
                position: "relative",
                pr: 0,
                "&:hover": { pr: "30px" },
                "&:hover .sort-icon": { opacity: 1 },
              }}
            >
              <span 
                className="date-rejected-table-header"
                style={{ 
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  flex: "0 1 auto"
                }}
              >
                Date of Rejection
              </span>
              {renderSortIcon("changed_date", "Date of Rejection")}
            </Box>
          ),
          valueGetter: (params) => {
            // Return the date as a Date object for proper sorting
            return new Date(params.row.date);
          },
          renderCell: (params) => (
            <span className="table-history-DateRejected-cell">
              {params.row.date}
            </span>
          ),
        },
        {
          field: "rejectedBy",
          headerName: "Rejected By",
          headerClassName: "orgName-column-header",
          headerAlign: "center",
          align: "left",
          flex: 2,
          minWidth: 280,
          sortable: false,
          renderHeader: () => (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.25,
                overflow: "hidden",
                width: "100%",
                position: "relative",
                pr: 0,
                "&:hover": { pr: "30px" },
                "&:hover .sort-icon": { opacity: 1 },
              }}
            >
              <span 
                className="orgName-column-header"
                style={{ 
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  flex: "0 1 auto"
                }}
              >
                Rejected By
              </span>
              {renderSortIcon("changed_by", "Rejected By")}
            </Box>
          ),
          renderCell: (params) => (
            <Tooltip
              title={<p style={{ fontSize: "12px" }}>{params.row.changedBy}</p>}
            >
              <span className="table-cell-history">{params.row.changedBy}</span>
            </Tooltip>
          ),
        },
        {
          field: "action",
          headerAlign: "center",
          headerName: "Actions",
          align: "center",
          flex: 1.5,
          minWidth: 220,
          sortable: false,
          filterable: false,
          renderCell: (params) => (
            <ReusableActions
              params={params}
              action={singleUserAction(params)}
            />
          ),
        },
      ]);
    }
  }, [updatedRows, currentPage, rejectedUserListData?.total, sortBy, sortOrder, nameSortMenuAnchor]);

  return (
    <div>
      <ReusableDataGrid
        columns={columns}
        rows={updatedRows}
        orgName={orgName || "Deloitte UK Innersource"}
        viewType={"Rejected History"}
        setCurrentPage={setCurrentPage}
        currentPage={currentPage}
        checkboxSelection={true}
        totalRecords={rejectedUserListData?.total || 0}
        isLoading={
          isRejectedUserListDataLoading ||
          isPlatformDataLoading ||
          isUpdateUserMembershipBulkLoading
        }
        multiUserAction={multiUserAction}
        customSearchControls={renderFilterFieldSelect}
        customSearchInput={renderDynamicSearchInput}
        tabDescription={
          "Users in Rejected have had their access request to the organization denied. These users will not be able to re-request access to the organization until their profile has been reset"
        }
      />
    </div>
  );
};

export default RejectedTab;
